import React, { Suspense, useMemo } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams, useSearchParams } from 'react-router-dom'

import { YStack } from 'tamagui'

import { getSlugFromPrettyUrl } from '@data/util/urlHelpers'

import { EventMetadata } from '@newComponents/Event/EventMetadata'
import { Card } from '@newComponents/Card'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Spinner } from '@newComponents/Spinner'
import { H2, Text } from '@newComponents/Text'

import { EventItem } from '@newComponents/Event/EventItem'
import { lazy as loadable } from '@loadable/component'

import { EventAttendContent } from './EventAttendContent'

import { EventAttendScreenQuery } from './__generated__/EventAttendScreenQuery.graphql'

const EventAttendFormDialog = loadable(
  () => import('../../modals/EventAttendForm/EventAttendFormDialog')
)

export const EventAttendScreen = () => {
  const params = useParams<'eventId'>()
  const [searchParams] = useSearchParams()

  const data = useLazyLoadQuery<EventAttendScreenQuery>(
    graphql`
      query EventAttendScreenQuery($slug: String!) {
        event: eventBySlug(slug: $slug) {
          name
          prettyUrl
          eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
            totalCount
          }

          owner {
            __typename

            ... on User {
              firstName
              lastName
            }

            ... on Guild {
              name
              slugId

              myMembership {
                membershipLevel
                status
              }
            }
          }

          myAttendance {
            paymentStatus
          }

          myTicketOrder {
            id
            status
          }

          attendeeForm {
            rowId
          }

          ...EventItemNew_event
          ...EventAttendContent_event
          ...EventMetadata_event
        }

        ...EventItemNew_query
        ...EventAttendContent_query
      }
    `,
    { slug: getSlugFromPrettyUrl(params.eventId) }
  )

  const name = useMemo(() => {
    if (!data.event) {
      return ''
    }

    switch (data.event.owner?.__typename) {
      case 'Guild': {
        return data.event.owner.name
      }

      case 'User': {
        return `${data.event.owner.firstName} ${data.event.owner.lastName}`
      }
    }

    return ''
  }, [data])

  const statusParam = searchParams.get('status')

  if (!data.event) {
    return null
  }

  const pageTitle =
    data.event.eventTicketingTiers.totalCount > 0 &&
    data.event.myAttendance?.paymentStatus !== 'SUCCEEDED'
      ? `Confirm Attendance and Select Tickets for ${data.event.name}`
      : `Confirm Attendance for ${data.event.name}`

  return (
    <>
      <EventMetadata title={pageTitle} event={data.event} />

      <GradientBackgroundScreen alignItems='center' gap='$8'>
        <EventItem
          event={data.event}
          query={data}
          maxWidth={600}
          width='100%'
          showAttendButton={false}
          displayFullDate
          displayVisibility={false}
        />

        {data.event.attendeeForm && statusParam !== 'NOT_ATTENDING' ? (
          <Card gap='$6' maxWidth={600} width='100%'>
            <Suspense
              fallback={
                <YStack
                  flex={1}
                  alignItems='center'
                  justifyContent='center'
                >
                  <Spinner size='$9' color='$primaryColor' />
                </YStack>
              }
            >
              <EventAttendFormDialog />
            </Suspense>
          </Card>
        ) : (
          <EventAttendContent event={data.event} query={data} maxWidth={600} width='100%' />
        )}
      </GradientBackgroundScreen>
    </>
  )
}

export default EventAttendScreen
